import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Building2,
  Handshake,
  Inbox,
  Megaphone,
  Search,
  Settings,
  Sparkles,
  UserCircle,
} from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { UserDropdown } from "@/components/UserDropdown";
import BrandSwitcher from "@/components/BrandSwitcher";
import CollaborationBalanceCard from "@/components/CollaborationBalanceCard";
import { AdvisorLogoIcon } from "@/components/icons/AdvisorLogo";
import { AssistantLauncher } from "@/components/Assistant";
import { cn } from "@/lib/utils";
import { MARKETPLACE_NAME } from "@/views/Marketplace/constants";

interface NavItem {
  to: string;
  icon: typeof Search;
  labelKey: string;
}

/**
 * Navigation is per role, not filtered from a shared list. A creator has no
 * reason to see "Discover creators", and a brand has no profile to edit — the
 * two sides use the same shell but genuinely different apps.
 *
 * Every entry here must reach a screen that does something. A nav item that
 * lands on a placeholder is a credibility leak in front of a client, so the
 * wallet item is gone until the payment clock exists to put behind it.
 */
const BRAND_NAV: NavItem[] = [
  { to: "/descubrir", icon: Search, labelKey: "marketplace.nav.discover" },
  { to: "/asistente", icon: Sparkles, labelKey: "marketplace.nav.assistant" },
  { to: "/campanas", icon: Megaphone, labelKey: "marketplace.nav.campaigns" },
  { to: "/colaboraciones", icon: Handshake, labelKey: "marketplace.nav.collaborations" },
  { to: "/marca", icon: Building2, labelKey: "marketplace.nav.brand" },
];

const CREATOR_NAV: NavItem[] = [
  { to: "/oportunidades", icon: Inbox, labelKey: "marketplace.nav.opportunities" },
  { to: "/colaboraciones", icon: Handshake, labelKey: "marketplace.nav.collaborations" },
  { to: "/asistente", icon: Sparkles, labelKey: "marketplace.nav.assistant" },
  { to: "/perfil", icon: UserCircle, labelKey: "marketplace.nav.profile" },
];

/**
 * The active item takes a steel fill and a 2.5px rail down its left edge. The
 * rail is what makes the state readable at a glance — a tinted background alone
 * is easy to miss against a white sidebar, and this is the only navigation the
 * app has.
 */
const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "relative flex items-center gap-3 rounded-nav px-3 py-2 text-meta transition-colors",
    isActive
      ? [
          "bg-steel-50 font-semibold text-steel-600",
          "before:absolute before:inset-y-2 before:left-0 before:w-[2.5px]",
          "before:rounded-[3px] before:bg-steel-600 before:content-['']",
        ]
      : "font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900",
  );

export default function MarketplaceShell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();

  const isCreator = user?.marketplaceRole === "CREATOR";
  const items = isCreator ? CREATOR_NAV : BRAND_NAV;

  // Read from the path rather than lifted state: the shell is a layout route,
  // so it never re-renders from the child that knows the id. "nueva" is the
  // create route, not a campaign.
  const campaignMatch = pathname.match(/^\/campanas\/([^/]+)/);
  const openCampaignId =
    campaignMatch && campaignMatch[1] !== "nueva" ? campaignMatch[1] : undefined;

  // The fullscreen assistant mounts its own conversation. Rendering the
  // launcher here too would put two on screen at once, and each one runs a
  // hydration fetch and arms the automatic approval resume — so the second
  // would re-fetch the thread on load and could answer an approval twice.
  const assistantIsFullscreen = pathname.startsWith("/asistente");

  const wordmark = (
    <div className="flex items-center gap-2.5">
      <AdvisorLogoIcon size="sm" className="text-steel-600" />
      <span className="text-meta font-bold tracking-tight text-steel-600">
        {MARKETPLACE_NAME}
      </span>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 md:flex-row">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 flex-col border-r border-neutral-200 bg-white md:flex">
        <div className="flex h-14 items-center border-b border-neutral-100 px-5">
          {wordmark}
        </div>

        {/* Brand-side only, and only above two brands — it renders nothing for
            a creator or a single-brand account. */}
        <BrandSwitcher enabled={!isCreator} />

        <nav className="flex-1 space-y-1 px-3 py-4">
          {items.map(({ to, icon: Icon, labelKey }) => (
            <NavLink key={to} to={to} className={linkClass}>
              <Icon className="size-4 shrink-0" />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>

        {/* What is committed across live collaborations. Renders nothing when
            there are none — a zero here reads as a broken widget. */}
        <div className="px-3">
          <CollaborationBalanceCard isCreator={isCreator} />
        </div>

        <div className="border-t border-neutral-100 px-3 py-3">
          <NavLink to="/settings" className={linkClass}>
            <Settings className="size-4 shrink-0" />
            {t("nav.settings")}
          </NavLink>
        </div>
      </aside>

      <header className="adv-glass-header sticky top-0 z-20 flex h-14 items-center justify-between border-b border-neutral-200 px-4 md:hidden">
        {wordmark}
        <UserDropdown />
      </header>

      <div className="flex-1 md:ml-56">
        {/* Glass, not solid white: the header sits over content that scrolls
            under it, and one of the system's three legal uses of blur. */}
        <header className="adv-glass-header sticky top-0 z-20 hidden h-14 items-center justify-end border-b border-neutral-200 px-6 md:flex">
          <UserDropdown />
        </header>

        {/* The assistant launcher floats over the bottom-right corner, so the
            page has to reserve its footprint or the last thing on any screen
            slides under it — the campaign wizard's "Siguiente" did exactly
            that. 24px offset + 48px tall, plus the mobile tab bar below it.
            Padded only while the launcher is actually mounted. */}
        <main
          className={cn(
            "pb-16 md:pb-0",
            !assistantIsFullscreen && "pb-36 md:pb-24",
          )}
        >
          <Outlet />
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-14 items-center justify-around border-t border-neutral-200 bg-white md:hidden">
        {items.map(({ to, icon: Icon, labelKey }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-0.5 px-3 py-1.5 text-micro font-medium transition-colors",
                isActive ? "text-steel-600" : "text-neutral-400",
              )
            }
          >
            <Icon className="size-5" />
            {t(labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Scoped to whatever campaign the user has open, so "this campaign"
          resolves without the model naming an id. Server-side the id is
          re-checked against the caller's brand profile regardless. */}
      {!assistantIsFullscreen && <AssistantLauncher campaignId={openCampaignId} />}
    </div>
  );
}
